import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { Assumptions, CompModel } from '../types/compensation';
import { formatCompact, formatDkk, formatPercent } from '../utils/format';
interface ResultCardProps {
  model: CompModel;
  assumptions: Assumptions;
}
export function ResultCard({
  model,
  assumptions
}: ResultCardProps) {
  return <div className="space-y-4" data-id="element-101" data-mp="21p0tfj3axtxm">
      {/* 1 — What is actually guaranteed */}
      <section aria-label="Guaranteed salary" className="rounded-card border border-line bg-ink-850 p-6 lg:p-8" data-id="element-102" data-mp="2pfo3a9ws2tmt">
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-security" data-id="element-103" data-mp="2ufafqh4s85uk">
          Guaranteed salary
        </p>
        <p className="tnum mt-3 text-[46px] font-semibold leading-none tracking-tightest text-paper lg:text-[58px]" data-id="element-104" data-mp="2kn4oxaq9nugy">
          {formatDkk(model.fixedAnnual)}
        </p>
        <p className="tnum mt-3 text-sm text-paper-muted" data-id="element-105" data-mp="38be6u1kczy2t">
          per year · {formatDkk(model.fixedMonthly)} / month
        </p>
      </section>

      {/* 2 — The upside you build */}
      <section aria-label="Performance and ownership upside" className="rounded-card border border-line bg-ink-900 p-6 lg:p-7" data-id="element-106" data-mp="1l0y37it7dbij">
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-paper-dim" data-id="element-107" data-mp="1ut3t8mzw9iv3">
          Then you build your upside
        </p>
        <p className="mt-1.5 text-sm text-paper-dim" data-id="element-108" data-mp="1q0u9jopdhf19">
          At the current allocation
        </p>

        <div className="mt-6 space-y-6" data-id="element-109" data-mp="23rpbeh1gevvc">
          <div data-id="element-110" data-mp="1yuytrpa03s7t">
            <div className="flex items-baseline justify-between gap-4" data-id="element-111" data-mp="1yv8nnny62jis">
              <p className="text-sm font-medium text-paper" data-id="element-112" data-mp="2drg54gi6x6ud">
                Commission at target
              </p>
              <p className="tnum text-[30px] font-semibold leading-none tracking-tight text-performance lg:text-[34px]" data-id="element-113" data-mp="28sd9tns0iezl">
                + {formatDkk(model.commissionOpportunity)}
              </p>
            </div>
            <p className="tnum mt-2 text-xs text-paper-dim" data-id="element-114" data-mp="efner2pwqzqm">
              {formatDkk(model.commissionCash)} allocated ×
              {assumptions.commissionMultiplier} · paid at 100% of target
            </p>
          </div>

          <div className="border-t border-line-soft pt-6" data-id="element-115" data-mp="184mherdnzqw8">
            <div className="flex items-baseline justify-between gap-4" data-id="element-116" data-mp="1d364iv3zv8q6">
              <p className="text-sm font-medium text-paper" data-id="element-117" data-mp="1i2ion83he1p2">Equity value</p>
              <p className="tnum text-[30px] font-semibold leading-none tracking-tight text-ownership lg:text-[34px]" data-id="element-118" data-mp="1i0fss7fndecf">
                + {formatDkk(model.equityValue)}
              </p>
            </div>
            <p className="tnum mt-2 text-xs text-paper-dim" data-id="element-119" data-mp="1r0uf9a4ckzhi">
              {formatDkk(model.equityCash)} allocated ×
              {assumptions.equityMultiplier} ≈{' '}
              {formatPercent(model.ownershipPercent, 2)} indicative ownership
            </p>
          </div>
        </div>
      </section>

      {/* 3 — Only now, the total */}
      <section aria-label="Total compensation value at target" className="rounded-card border border-paper/20 bg-ink-850 p-6 lg:p-7" data-id="element-120" data-mp="2aqeoglock9q2">
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-paper-dim" data-id="element-121" data-mp="3v6vxuq3xmzfj">
          At 100% of target
        </p>
        <p className="tnum mt-3 text-sm leading-relaxed text-paper-muted" data-id="element-122" data-mp="3laj45s8e0lks">
          {formatCompact(model.fixedAnnual)} salary +{' '}
          {formatCompact(model.commissionOpportunity)} commission +{' '}
          {formatCompact(model.equityValue)} equity
        </p>
        <p className="tnum mt-2 text-[30px] font-semibold leading-none tracking-tightest text-paper lg:text-[34px]" data-id="element-123" data-mp="hw6q3dc6v4mf">
          = {formatCompact(model.totalPackage)}
        </p>
        <p className="mt-2 text-sm text-paper-muted" data-id="element-124" data-mp="82pvi7489lhp">
          total compensation value
        </p>
        <p className="tnum mt-4 border-t border-line-soft pt-4 text-xs text-paper-dim" data-id="element-125" data-mp="3565yb22xmag">
          Cash at target: {formatCompact(model.cashPayout)} · equity is not
          deducted from cash
        </p>
      </section>

      <div className="flex flex-wrap items-center gap-2 text-xs text-paper-dim" data-id="element-126" data-mp="gc3nm6xrk8xl">
        <span className="rounded-full border border-line px-3 py-1.5" data-id="element-127" data-mp="ge6oxn4d9ohm">
          {formatCompact(model.fixedAnnual)} guaranteed
        </span>
        <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-128" data-mp="6efpr84v2lb7" />
        <span className="rounded-full border border-line px-3 py-1.5" data-id="element-129" data-mp="6gaekxj96xkk">
          + performance ×{assumptions.commissionMultiplier}
        </span>
        <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-130" data-mp="z3mqmdrsfmzm" />
        <span className="rounded-full border border-line px-3 py-1.5" data-id="element-131" data-mp="141eqpv2l6few">
          + ownership ×{assumptions.equityMultiplier}
        </span>
        <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-132" data-mp="pc0iwrens61m" />
        <span className="rounded-full border border-paper/25 px-3 py-1.5 text-paper" data-id="element-133" data-mp="ubd1gzyhyqye">
          {formatCompact(model.totalPackage)} at target
        </span>
      </div>
    </div>;
}